import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Share2 } from 'lucide-react';
import { PageTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';
import { HubScopeToggle } from '@/components/layout/HubLayout';
import { useRegisterPageChrome } from '@/components/layout/TakeoverShell/page-chrome';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { ROUTES } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { SharingPage } from '@/pages/security/SharingPage';
import {
  DEFAULT_SHARING_SCOPE,
  SHARING_SCOPES,
  SHARING_SCOPE_LABELS,
  type SharingScope,
} from './sharing-hub-config';

const SHARING_HUB_DESCRIPTION = 'Review and manage shared resources for this organization.';

/**
 * Connect → Sharing. Personal / organization scope toggle with no Security
 * framing — the page title is just "Sharing".
 */
export function SharingHubPage() {
  const navigate = useNavigate();
  const slug = useActiveOrgSlug();
  const { scope: scopeParam } = useParams();
  const { activeOrganization } = useOrganization();
  const chromeActive = useRegisterPageChrome({
    title: 'Sharing',
    description: SHARING_HUB_DESCRIPTION,
  });

  const scope: SharingScope =
    scopeParam === 'organization' || scopeParam === 'personal' ? scopeParam : DEFAULT_SHARING_SCOPE;

  if (!slug) {
    return <Navigate to={ROUTES.settings} replace />;
  }

  const orgRoutes = ROUTES.org(slug);

  return (
    <div
      className={cn(
        'mx-auto flex h-full w-full max-w-5xl flex-col px-4 pb-6 pt-3 md:px-6 md:py-8',
        chromeActive && 'max-md:pt-2',
      )}
    >
      <div className={cn('mb-4 md:mb-6', chromeActive && 'max-md:hidden')} data-testid="hub-intro">
        <PageTitle className="hidden md:block">Sharing</PageTitle>
        <PageDescription className={cn('mt-0 md:mt-1', chromeActive && 'max-md:hidden')}>
          {SHARING_HUB_DESCRIPTION}
        </PageDescription>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <HubScopeToggle
          available={SHARING_SCOPES}
          active={scope}
          labels={SHARING_SCOPE_LABELS}
          onChange={(next) => navigate(orgRoutes.sharingHubScope(next))}
        />
        {scope === 'organization' && activeOrganization && (
          <span className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground">
            <Share2 className="size-3.5" aria-hidden="true" />
            <span className="flex size-4 items-center justify-center rounded bg-primary/20 text-3xs font-bold text-primary">
              {activeOrganization.name[0]}
            </span>
            {activeOrganization.name}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1 overflow-y-auto">
        <HubReadGuard permission={scope === 'organization' ? 'org:read' : undefined}>
          <SharingPage embedded />
        </HubReadGuard>
      </div>
    </div>
  );
}
